import { useEffect, useState } from 'react';
import { Smartphone, Share, X } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = 'pwa_prompt_dismissed_at';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isMobile = () => window.matchMedia('(max-width: 768px)').matches || /android|iphone|ipad|ipod/i.test(window.navigator.userAgent);
const snoozed = () => Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < SNOOZE_MS;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(window.__deferredInstallPrompt || null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile() || snoozed()) return;
    const onReady = () => setDeferred(window.__deferredInstallPrompt);
    window.addEventListener('pwa-install-ready', onReady);
    const t = setTimeout(() => setVisible(true), 2500);
    return () => { clearTimeout(t); window.removeEventListener('pwa-install-ready', onReady); };
  }, []);

  if (!visible || (!deferred && !isIOS())) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    window.__deferredInstallPrompt = null;
    if (outcome !== 'accepted') localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-3 bottom-3 z-50 border-2 border-foreground bg-background p-4 animate-in slide-in-from-bottom-4 duration-300" style={{ boxShadow: '4px 4px 0 0 hsl(var(--primary))' }} data-testid="pwa-install-prompt">
      <button onClick={dismiss} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground" aria-label="Dismiss" data-testid="pwa-install-dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-heading text-sm uppercase tracking-widest mb-1">ADD TO HOME SCREEN</div>
          {deferred ? (
            <>
              <p className="text-xs text-muted-foreground mb-3">Open your Life Profile in one tap, full screen, like a native app.</p>
              <Button size="sm" onClick={install} className="uppercase tracking-widest text-xs" data-testid="pwa-install-btn">Install app</Button>
            </>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid="pwa-ios-instructions">
              Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" /> Share in Safari, then <span className="font-semibold text-foreground">Add to Home Screen</span>.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
